<script>
  import {onMount, createEventDispatcher, onDestroy} from 'svelte'
  import {media} from 'svelte-match-media'
  import gsap from 'gsap'
  import ScrollTrigger from 'gsap/ScrollTrigger'
  import { ga } from '@beyonk/svelte-google-analytics'

  export let id = ''
  export let title = 'See More'
  export let small = false
  export let groupMode = false
  export let expandedOnDesktop = false
  export let hideTitleOnDesktop = true
  export let open = expandedOnDesktop && window.innerWidth >= 768 ? true : false
  export let groupTriggerEl = null
  export let index = 0
  export let triggerStart = 'top 70%'
  export let fireChangeEvent = true

  $: triggerEl = groupTriggerEl ? groupTriggerEl : accordionEl

  $: if(groupTriggerEl != null) {
    st.refresh()
  }

  const dispatch = createEventDispatcher()
  let content, contentWrap, accordionEl, coverEl, st, tl;

  $: if(open !== undefined) {
    const contentHeight = gsap.getProperty(content, 'height')
    gsap.fromTo(contentWrap, {height: open ? 0 : contentHeight}, {
      duration: 0.75,
      ease: 'power2.inOut',
      height: open ? contentHeight : 0,
      onComplete: () => {
        if(open) gsap.set(contentWrap, {height: 'auto'})
        if(fireChangeEvent) {
          const event = new Event('accordion-change')
          document.dispatchEvent(event)
        }
      }
    })
  }

  onMount (() => {

    if(!open) {
      const delay = index * .08
      tl = gsap.timeline()
      tl.fromTo(accordionEl, {
        clipPath: `inset(15px 0px)`,
      },{
        clipPath: 'inset(0rem 0px)',
        ease: 'power2.inOut',
        duration: 0.75
      }, delay)
      tl.fromTo(accordionEl.querySelector('.inner'), {
        opacity: 0
      },{
        opacity: 1,
        ease: 'power2.inOut',
        duration: 0.75
      }, delay)
      tl.fromTo(coverEl, {
        top: '50%',
        height: '0%',
        scaleX: 0,
        transformOrigin: '0 50%',
      },{
        scaleX: 1,
        transformOrigin: '0 50%',
        ease: 'power2.out',
        duration: 0.2
      }, delay)
      tl.to(coverEl, {
        top: '-1%',
        height: '100%',
        ease: 'power2.inOut',
        duration: 0.55
      }, delay + 0.2)
      tl.to(coverEl, {
        opacity: 0,
        duration: .1,
      }, delay + 0.75)
      st = ScrollTrigger.create({
        trigger: () => triggerEl,
        animation: tl,
        start: triggerStart,
      })
    }
  })

  onDestroy(() => {
    st?.kill()
    tl?.kill()
  })

  function toggle () {
    if(!groupMode) open = !open
    dispatch('toggle')
    try {
      ga.addEvent('toggle_accordion', {
        id, open
      })
    } catch(e) {
      console.log(e)
    }
  }

</script>
<div class="accordion" class:expanded-on-desktop={expandedOnDesktop} class:hide-title-on-desktop={expandedOnDesktop && hideTitleOnDesktop} class:open={open} class:small={small} bind:this={accordionEl}>
  <div class="inner">
    <div class="btn-wrap">
      {#if expandedOnDesktop && $media.desktop}
        <span class="title">{title}</span>
      {:else}
        <button type="button" :disabled={expandedOnDesktop} on:click={toggle} class="custom toggle-btn"><span class="title">{title}</span><span class="icon"><span class="line line1" /><span class="line line2" /></span></button>
      {/if}
    </div>
    <div class="content-wrap" bind:this={contentWrap}>
      <div class="content" bind:this={content}><slot></slot></div>
    </div>
  </div>
  <div class="cover" bind:this={coverEl}></div>
</div>

<style>
  .accordion {
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    box-shadow: 0 0 0 white inset;
    transition: 200ms linear box-shadow;
    position: relative;
  }
  .accordion > .inner {
    padding: 16px 0;
  }
  .cover {
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    position: absolute;
    left: 0;
    right: 0;
    z-index: -1;
    top: 0;
    box-sizing: content-box;
    pointer-events: none;
  }
  .accordion.open .toggle-btn .icon .line1 {
    transform: translate3d(0, 9px, 0) rotateZ(180deg);
  }
  .accordion.open .toggle-btn .icon .line2 {
    transform: translate3d(0, 9px, 0) rotateZ(0deg);
  }
  .accordion.open {
    box-shadow: 0 2px 0 white inset;
  }
  @media(min-width: 768px) {
    .accordion.hide-title-on-desktop .btn-wrap {
      display: none;
    }
    .accordion.open {
      box-shadow: 0 4px 0 white inset;
    }
    .accordion.expanded-on-desktop {
      box-shadow: 0 4px 0 white inset;
    }
    .accordion.expanded-on-desktop .icon {
      display: none;
    }
    .accordion.expanded-on-desktop button {
      pointer-events: none;
      cursor: text;
    }
  }
  .toggle-btn {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .btn-wrap .title {
    font-weight: bold;
    font-size: 1.125rem;
    padding-right: 20px;
    line-height: 1;
  }
  .icon {
    display: block;
    width: 18px;
    height: 18px;
    position: relative;
    transform: translateY(-1px);
    flex: 0 0 auto;
  }
  .line {
    height: 1px;
    width: 18px;
    background: white;
    position: absolute;
    transition: 300ms cubic-bezier(0.645, 0.045, 0.355, 1) transform;
  }
  .line1 {
    transform: translate3d(0, 9px, 0);
  }
  .line2 {
    transform: translate3d(0, 9px, 0) rotateZ(90deg);
  }
  .content {
    padding: 20px 0;
  }
  .content-wrap {
    overflow: hidden;
    height: 0;
  }
</style>